import { DownloadOutlined } from '@ant-design/icons';
import { Spin, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { InfoBlock } from 'app/components/commons/InfoBlock/InfoBlock';
import { getVCCStatus } from 'app/components/pages/Extranet/ExtranetBookings/Modern/_components/BookingDetails/_components/VirtualCard/utils/virtualCard.utils';
import { useDownloadBill } from 'app/hooks/data/bills/useDownloadBill';
import { BillStatus } from 'app/typings/bills';
import type { BookingDetails } from 'app/typings/core/admin/bookings/booking';
import { SUPPORTED_PSP_MODE } from 'app/typings/core/common/enum/supportedPspMode';

import styles from './HotelPayment.module.scss';
import { formatVccStatus } from './utils';

type Props = {
  bookingDetails: BookingDetails;
};

export const HotelPayment = ({ bookingDetails }: Props) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const isIssuingBooking = !!bookingDetails.issuingCardId;

  const { label: vccLabel = '', color: vccColor = '' } = isIssuingBooking
    ? formatVccStatus(bookingDetails.vccStatus)
    : {};

  const status = isIssuingBooking
    ? getVCCStatus(bookingDetails, currencyFormatter)
    : null;

  const shouldShowToCharge = bookingDetails.issuingBalance > 0;
  const shouldShowToRefund = bookingDetails.issuingBalance < 0;

  const { billReference, billId, billStatus } = bookingDetails.payments[0];

  const canDownloadBill =
    !!billId &&
    (billStatus === BillStatus.GENERATED || billStatus === BillStatus.SENT);

  const { mutate: downloadBill, isPending: isDownloadingBill } =
    useDownloadBill();

  const issuingRows = [
    {
      label: 'VCC Status',
      value: <Tag color={vccColor}>{vccLabel}</Tag>,
    },
    {
      label: 'To charge',
      value: currencyFormatter(
        shouldShowToCharge ? bookingDetails.issuingBalance : 0
      ),
    },
    {
      label: 'To refund',
      value: currencyFormatter(
        shouldShowToRefund ? -bookingDetails.issuingBalance : 0
      ),
    },
    ...(status?.history.length
      ? status.history.map((item) => ({
          label: 'History',
          value: item,
        }))
      : []),
  ];

  const handleDownloadBill = async () => {
    if (!canDownloadBill || isDownloadingBill) {
      return;
    }

    downloadBill(billId);
  };

  return (
    <InfoBlock
      title="Hotel payment"
      rows={[
        {
          label: 'Bill reference',
          value: (
            <>
              {billReference ? (
                <Tooltip title={canDownloadBill ? 'Download bill' : undefined}>
                  <div
                    className={classNames(styles.billReference, {
                      [styles.isDownloadable]: canDownloadBill,
                      [styles.isDownloading]: isDownloadingBill,
                    })}
                    onClick={canDownloadBill ? handleDownloadBill : undefined}
                  >
                    {canDownloadBill && (
                      <>
                        <Spin spinning={isDownloadingBill} size="small" />
                        {!isDownloadingBill && <DownloadOutlined />}
                      </>
                    )}
                    <span>{billReference}</span>
                  </div>
                </Tooltip>
              ) : (
                '-'
              )}
            </>
          ),
        },
        {
          label: 'Payment method',
          value:
            bookingDetails.payments[0].pspMode === SUPPORTED_PSP_MODE.ISSUING
              ? 'Virtual card (VCC)'
              : 'Payout',
        },
        ...(isIssuingBooking ? issuingRows : []),
      ]}
    />
  );
};
